<template>
  <div class="digital-human kye-contrast-off"
    :class="{
      'is-hover': state === 'hover',
      'is-click': state === 'click'
    }"
  >
    <!-- 热点区域：鼠标在此区域内才触发悬浮/点击效果 -->
    <div class="digital-human-hotspot"
      @mouseenter="handleHotspotEnter"
      @mouseleave="handleHotspotLeave"
      @mousedown="handleHotspotDown"
      @mouseup="handleHotspotUp"
    ></div>
    <!-- 默认图片 -->
    <img
      v-if="defaultImgSrc"
      class="digital-human-default"
      :src="defaultImgSrc"
      alt=""
    >
    <!-- 悬浮图片 -->
    <img
      v-if="hoverImgSrc"
      class="digital-human-hover"
      :src="hoverImgSrc"
      alt=""
    >
    <!-- 点击状态图片（可选） -->
    <img
      v-if="clickImgSrc"
      class="digital-human-click"
      :src="clickImgSrc"
      alt=""
    >
  </div>
</template>

<script>
  const PLAY_TIMEOUT = 3000 // 显示时间
  const LOG_PREFIX = '[数字人]: '

  export default {
    name: 'DigitalHuman',
    props: {
      code: String, // 数字人编码
      enableClick: { // 是否启用点击效果
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        baseSrc: 'https://erphostjs.kye-erp.com/public/digital-human/', // 图片基础路径
        hasHover: true, // 是否存在hover状态图
        // hasClick: false, // 是否存在click状态图
        state: 'default', // 当前状态: default | hover | click

        hoverImgSrc: '', // hover图片地址
        nextHoverImgSrc: '', // 下次hover图片地址
        nextHoverLoad: false, // 下次hover图片是否加载完成

        clickImgSrc: '', // click图片地址
        nextClickImgSrc: '', // 下次click图片地址
        nextClickLoad: false, // 下次click图片是否加载完成
      }
    },
    computed: {
      baseImgSrc () {
        return this.code ? this.baseSrc + this.code : ''
      },
      defaultImgSrc () {
        return this.baseImgSrc ? this.baseImgSrc + '.png?t=' + Date.now() : ''
      },
      hasClick () {
        return this.enableClick
      }
    },
    watch: {
      code: {
        handler () {
          this.state = 'default'
          this.nextHoverImgSrc = ''
          this.nextClickImgSrc = ''
          this.nextHoverLoad = false
          this.nextClickLoad = false
          // 这样图片会提前加载到缓存，悬浮时直接显示无白屏
          this.$nextTick(() => {
            if (this.hasHover) {
              const src = this.getHoverSrc()
              this.loadImage(src).then(() => {
                this.hoverImgSrc = src
                this.nextHoverImgSrc = src
                this.nextHoverLoad = true
                this.handleHotspotEnter()
              })
            }
            if (this.hasClick) {
              const src = this.getClickSrc()
              this.loadImage(src).then(() => {
                this.clickImgSrc = src
                this.nextClickImgSrc = src
                this.nextClickLoad = true
              })
            }
          })
        },
        immediate: true
      },
      $route (val) {
        if (val.path === '/') {
          this.handleHotspotEnter()
        }
      }
    },
    methods: {
      getHoverSrc () {
        return this.baseImgSrc ? this.baseImgSrc + '_hover.png?t=' + Date.now() : ''
      },
      getClickSrc () {
        return this.baseImgSrc ? this.baseImgSrc + '_click.png?t=' + Date.now() : ''
      },
      loadImage (src) {
        return new Promise((resolve, reject) => {
          const img = new Image()
          const timer = setTimeout(() => reject(new Error(LOG_PREFIX + '加载超时')), 10000)

          img.onload = () => {
            clearTimeout(timer)
            resolve(img)
          }
          img.onerror = () => {
            clearTimeout(timer)
            reject(new Error(LOG_PREFIX + '加载失败'))
          }

          img.src = src
        })
      },
      // 热点区域事件处理
      handleHotspotEnter () {
        if (this.hasHover && this.state === 'default') {
          if (this.nextHoverLoad && this.nextHoverImgSrc === this.hoverImgSrc) {
            const url = this.getHoverSrc()
            this.state = 'hover'
            this.nextHoverImgSrc = url
            this.nextHoverLoad = false
            // 显示hover图片3秒后切换回默认状态
            setTimeout(() => {
              this.state = 'default'
              this.hoverImgSrc = url
            }, PLAY_TIMEOUT)
          }

          // 更新下次hover图片地址
          if (!this.nextHoverLoad) {
            this.loadImage(this.nextHoverImgSrc).then(() => {
              this.nextHoverLoad = true
            }).catch(() => {
              this.nextHoverLoad = false
            })
          }
        }
      },
      handleHotspotLeave () {},
      handleHotspotDown () {
        if (this.hasClick && this.state !== 'click') {
          if (this.nextClickLoad && this.nextClickImgSrc === this.clickImgSrc) {
            const url = this.getClickSrc()
            this.state = 'click'
            this.nextClickImgSrc = url
            this.nextClickLoad = false
            // 显示click图片3秒后切换回默认状态
            setTimeout(() => {
              this.state = 'default'
              this.clickImgSrc = url
            }, PLAY_TIMEOUT)
          }

          // 更新下次click图片地址
          if (!this.nextClickLoad) {
            this.loadImage(this.nextClickImgSrc).then(() => {
              this.nextClickLoad = true
            }).catch(() => {
              this.nextClickLoad = false
            })
          }
        }
      },
      handleHotspotUp () {}
    }
  }
</script>

<style lang="scss" scoped>
.digital-human {
  img {
    display: block;
    width: 100%;
  }
  // 非默认状态的图片绝对定位覆盖
  .digital-human-hover,
  .digital-human-click {
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 0;
    pointer-events: none;
  }
  // 热点区域：固定位置触发悬浮/点击效果
  .digital-human-hotspot {
    position: absolute;
    bottom: 0px;
    right: 26px;
    width: 100px;
    height: 170px;
    z-index: 101;
    cursor: pointer;
  }
  // 状态切换：通过JS动态添加class控制显隐，带opacity过渡
  &.is-hover {
    .digital-human-default {
      opacity: 0;
    }
    .digital-human-hover {
      opacity: 1;
    }
  }
  &.is-click {
    .digital-human-default {
      opacity: 0;
    }
    .digital-human-click {
      opacity: 1;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/layout/pages/home/digital-human.vue